import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useLanguage } from '../contexts/LanguageContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Megaphone, Mail, MessageSquare, Bell, Loader2, CheckCircle, AlertCircle, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageSquare },
  { id: 'in_app', label: 'In-App', icon: Bell },
];

const ShipmentBroadcastDialog = ({ open, onOpenChange, shipmentId, shipmentName }) => {
  const { t } = useLanguage();
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [channels, setChannels] = useState(['in_app']);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [recipientsList, setRecipientsList] = useState(null);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  const fetchRecipients = async (broadcastId) => {
    setLoadingRecipients(true);
    try {
      const res = await api.get(`${API}/broadcast/${broadcastId}/recipients`);
      setRecipientsList(res.data);
    } catch (err) {
      console.error('Recipients fetch error:', err);
      setRecipientsList(null);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleShowRecipients = () => {
    if (!showRecipients && result?.id) {
      setShowRecipients(true);
      fetchRecipients(result.id);
    } else {
      setShowRecipients(!showRecipients);
    }
  };

  const fetchDefaultChannels = async () => {
    try {
      const res = await api.get(`${API}/company/settings`);
      const configured = res.data?.communication?.broadcast_channels;
      if (Array.isArray(configured) && configured.length > 0) {
        setChannels(configured);
      }
    } catch {
      // Fallback: keep default channels
    }
  };

  const resetForm = useCallback(() => {
    setSubject(`Notification — ${shipmentName || ''}`);
    setMessage('');
    setChannels(['email', 'whatsapp']);
    setPreview(null);
    setResult(null);
    setError('');
    setRecipientsList(null);
    setShowRecipients(false);
  }, [shipmentName]);

  useEffect(() => {
    if (open && shipmentId) {
      resetForm();
      fetchPreview();
      fetchDefaultChannels();
    }
  }, [open, shipmentId]);

  const fetchPreview = async () => {
    setLoadingPreview(true);
    try {
      const res = await api.get(`${API}/broadcast/preview`, {
        params: { target: 'by_shipment', shipment_id: shipmentId }
      });
      setPreview(res.data);
    } catch (err) {
      console.error('Preview error:', err);
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const toggleChannel = (channelId) => {
    setChannels(prev =>
      prev.includes(channelId)
        ? prev.filter(c => c !== channelId)
        : [...prev, channelId]
    );
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      setError(t('subjectAndMessageRequired'));
      return;
    }
    if (channels.length === 0) {
      setError(t('selectAtLeastOneChannel'));
      return;
    }

    setSending(true);
    setError('');
    try {
      const res = await api.post(`${API}/broadcast/send`, {
        subject: subject.trim(),
        message: message.trim(),
        channels,
        recipient_filter: {
          target: 'by_shipment',
          shipment_id: shipmentId
        }
      });
      setResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : t('errorSending'));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-accent" />
            {t('broadcast')} — {shipmentName}
          </DialogTitle>
          <DialogDescription>
            {t('broadcastSendNotif')}
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="py-6 text-center space-y-3">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
            <p className="font-medium text-lg">{t('broadcastSent')}</p>
            <p className="text-sm text-muted-foreground">
              {result.total_recipients} {t('recipientsTargeted')}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={handleShowRecipients}
              className="gap-2"
            >
              <Users className="h-4 w-4" />
              {showRecipients ? t('hideRecipients') : t('showRecipients')}
            </Button>

            {showRecipients && (
              <div className="text-left mt-3">
                {loadingRecipients ? (
                  <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('loading')}
                  </div>
                ) : recipientsList?.status === 'sending' ? (
                  <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('sendingInProgress')}
                  </div>
                ) : recipientsList?.recipients?.length > 0 ? (
                  <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
                    {recipientsList.recipients.map((r, i) => {
                      const ChannelIcon = r.channel === 'email' ? Mail : r.channel === 'whatsapp' ? MessageSquare : Bell;
                      const isSent = r.status === 'sent';
                      return (
                        <div key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
                          <span className="font-medium truncate min-w-0 flex-1">
                            {r.name || r.contact}
                          </span>
                          <span className="text-muted-foreground truncate text-xs">
                            {r.name ? r.contact : ''}
                          </span>
                          <ChannelIcon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <Badge
                            variant={isSent ? 'default' : 'destructive'}
                            className="text-xs flex-shrink-0"
                          >
                            {isSent ? t('sent') : t('failed')}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-2">{t('noRecipientsFound')}</p>
                )}
              </div>
            )}

            <Button variant="outline" onClick={() => onOpenChange(false)}>{t('close')}</Button>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Preview */}
            {loadingPreview ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('loadingRecipients')}
              </div>
            ) : preview ? (
              <div className="flex items-center gap-2 p-3 bg-accent/10 border border-accent/20 rounded-lg">
                <Users className="h-4 w-4 text-accent" />
                <span className="text-sm font-medium text-blue-900">
                  {preview.total} {t('recipients')}
                </span>
                <div className="flex gap-2 ml-auto">
                  {preview.email > 0 && (
                    <Badge variant="outline" className="text-xs">{preview.email} emails</Badge>
                  )}
                  {preview.whatsapp > 0 && (
                    <Badge variant="outline" className="text-xs">{preview.whatsapp} WhatsApp</Badge>
                  )}
                </div>
              </div>
            ) : null}

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="broadcast-subject">{t('subjectLabel')}</Label>
              <Input
                id="broadcast-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('subjectPlaceholder')}
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="broadcast-message">{t('messageLabel')}</Label>
              <Textarea
                id="broadcast-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('messagePlaceholder')}
                rows={4}
              />
            </div>

            {/* Channels */}
            <div className="space-y-2">
              <Label>{t('broadcastChannels')}</Label>
              <div className="flex gap-4">
                {CHANNELS.map(({ id, label, icon: Icon }) => (
                  <label key={id} className="flex items-center gap-2 cursor-pointer">
                    <Checkbox
                      checked={channels.includes(id)}
                      onCheckedChange={() => toggleChannel(id)}
                    />
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>{t('cancel')}</Button>
              <Button onClick={handleSend} disabled={sending}>
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('sending')}
                  </>
                ) : (
                  <>
                    <Megaphone className="h-4 w-4 mr-2" />
                    {t('send')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShipmentBroadcastDialog;
